import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY SEQUENCING BOARD (D1)
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  bg: theme?.colors?.background?.surface || "#ffffff",
  border: theme?.colors?.borders?.default || "#d6dee4",
  borderStrong: theme?.colors?.borders?.panel || "#cfd8dd",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  caneva: theme?.colors?.text?.caneva || "#c24c2c",
  green: theme?.colors?.text?.green || "#1A8C74",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",

  shadowXs: theme?.shadows?.xs || "0 1px 3px rgba(11,39,53,0.08)",
  shadowSm: theme?.shadows?.sm || "0 3px 10px rgba(11,39,53,0.08)",

  radiusLg: theme?.borders?.radius?.lg || "6px",
  radiusXl: theme?.borders?.radius?.xl || "8px",
};

/* ============================================================================
   MOCK DATA
============================================================================ */

const stablePackages = [
  {
    id: "p1",
    code: "A",
    name: "Préparation plateforme",
    type: "Lot technique",
    logic: "Par zone",
    status: "Confirmé",
    stage: "Préparation",
    discipline: "Réseaux",
    zone: "Plateforme",
    itemsCount: 3,
    effortMix: { materials: 1, labor: 2, equipment: 0 },
    dependencyIds: [],
    downstreamIds: ["p2", "p3"],
    executionMode: "Départ",
    criticality: "Modérée",
    graph: { col: 0, row: 1 },
  },
  {
    id: "p2",
    code: "B",
    name: "Réseaux primaires DN1000",
    type: "Lot technique",
    logic: "Par lot métier",
    status: "Confirmé",
    stage: "Infrastructure",
    discipline: "Réseaux",
    zone: "Tronçon primaire",
    itemsCount: 4,
    effortMix: { materials: 2, labor: 1, equipment: 1 },
    dependencyIds: ["p1"],
    downstreamIds: ["p5"],
    executionMode: "Séquentiel",
    criticality: "Forte",
    graph: { col: 1, row: 0 },
  },
  {
    id: "p3",
    code: "C",
    name: "Ouvrage principal – GC",
    type: "Sous-ensemble",
    logic: "Par zone",
    status: "Confirmé",
    stage: "Infrastructure",
    discipline: "Génie civil",
    zone: "Bloc traitement",
    itemsCount: 5,
    effortMix: { materials: 2, labor: 3, equipment: 0 },
    dependencyIds: ["p1"],
    downstreamIds: ["p4", "p5"],
    executionMode: "Parallèle",
    criticality: "Forte",
    graph: { col: 1, row: 2 },
  },
  {
    id: "p4",
    code: "D",
    name: "Bloc traitement – ligne 1",
    type: "Sous-système",
    logic: "Par système",
    status: "Confirmé",
    stage: "Équipements",
    discipline: "Process",
    zone: "Ligne 1",
    itemsCount: 4,
    effortMix: { materials: 1, labor: 1, equipment: 2 },
    dependencyIds: ["p3"],
    downstreamIds: ["p6"],
    executionMode: "Séquentiel",
    criticality: "Critique",
    graph: { col: 2, row: 2 },
  },
  {
    id: "p5",
    code: "E",
    name: "Poste électrique principal",
    type: "Système",
    logic: "Par système",
    status: "Confirmé",
    stage: "Équipements",
    discipline: "Électricité",
    zone: "Bloc utilités",
    itemsCount: 3,
    effortMix: { materials: 0, labor: 1, equipment: 2 },
    dependencyIds: ["p2", "p3"],
    downstreamIds: ["p6"],
    executionMode: "Parallèle",
    criticality: "Critique",
    graph: { col: 2, row: 0 },
  },
  {
    id: "p6",
    code: "F",
    name: "Essais et mise en route",
    type: "Séquence",
    logic: "Par système",
    status: "À surveiller",
    stage: "Finalisation",
    discipline: "Process",
    zone: "Bloc traitement",
    itemsCount: 2,
    effortMix: { materials: 0, labor: 2, equipment: 0 },
    dependencyIds: ["p4", "p5"],
    downstreamIds: [],
    executionMode: "Clôture",
    criticality: "Critique",
    graph: { col: 3, row: 1 },
  },
];

const stageOrder = ["Préparation", "Infrastructure", "Équipements", "Finalisation"];

/* ============================================================================
   HELPERS
============================================================================ */

const getDisciplineColor = (discipline) => {
  switch (discipline) {
    case "Génie civil":
      return T.blueText;
    case "Réseaux":
      return T.greenText;
    case "Process":
      return T.orangeText;
    case "Électricité":
      return T.greyText;
    default:
      return T.soft;
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case "Confirmé":
      return T.green;
    case "À surveiller":
      return "#B8621B";
    case "À consolider":
      return T.caneva;
    default:
      return T.soft;
  }
};

const getCriticalityColor = (criticality) => {
  switch (criticality) {
    case "Critique":
      return T.caneva;
    case "Forte":
      return "#B8621B";
    case "Modérée":
      return T.green;
    default:
      return T.soft;
  }
};

const getFlowTone = (pkg) => {
  if (!pkg.dependencyIds?.length) return "Départ";
  if (!pkg.downstreamIds?.length) return "Sortie";
  if ((pkg.dependencyIds?.length || 0) >= 2 || (pkg.downstreamIds?.length || 0) >= 2) {
    return "Nœud";
  }
  return "Flux";
};

const getLinkType = (sourcePkg, targetPkg) => {
  if (
    sourcePkg.criticality === "Critique" ||
    targetPkg.criticality === "Critique"
  ) {
    return "critical";
  }

  if (sourcePkg.discipline !== targetPkg.discipline) {
    return "interface";
  }

  return "flow";
};

/* ============================================================================
   UI BLOCKS
============================================================================ */

const HeaderBlock = ({ packageCount, criticalCount, startCount, viewMode, setViewMode }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 14,
      paddingBottom: 18,
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.72rem",
          lineHeight: 1.1,
          color: T.verySoft,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        Activity sequencing
      </div>

      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "1.03rem",
          lineHeight: 1.14,
          color: T.text,
          fontWeight: 600,
        }}
      >
        Organisation de l’enchaînement des packages
      </div>
    </div>

    <TopToolbar
      leftContent={
        <>
          <MetricInline label="Packages" value={packageCount} />
          <MetricInline
            label="Packages critiques"
            value={criticalCount}
            valueColor={criticalCount > 0 ? T.caneva : T.text}
          />
          <MetricInline
            label="Points de départ"
            value={startCount}
            valueColor={T.corelia}
          />
        </>
      }
      centerContent={<ViewSwitch viewMode={viewMode} setViewMode={setViewMode} />}
      rightContent={
        <>
          <LegendDot color={T.green} label="Confirmé" />
          <LegendDot color="#B8621B" label="À surveiller" />
          <LegendDot color={T.caneva} label="Critique" />
        </>
      }
    />
  </div>
);

const GraphToolbar = ({ nodeCount, linkCount, graphFilter, setGraphFilter }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 12,
      paddingTop: 8,
      paddingBottom: 16,
    }}
  >
    <TopToolbar
      leftContent={
        <>
          <MetricInline label="Nœuds" value={nodeCount} />
          <MetricInline label="Liaisons" value={linkCount} />
          <MetricInline label="Timeline" value="Sans dates" valueColor={T.corelia} />
        </>
      }
      centerContent={
        <GraphFilterSwitch graphFilter={graphFilter} setGraphFilter={setGraphFilter} />
      }
      rightContent={
        <>
          <LinkLegendItem color={T.corelia} dashed={false} label="Flux" />
          <LinkLegendItem color={T.orangeText} dashed label="Interface" />
          <LinkLegendItem color={T.caneva} dashed={false} thick label="Critique" />
        </>
      }
    />
  </div>
);

const TopToolbar = ({ leftContent, centerContent, rightContent }) => (
  <div
    style={{
      width: "100%",
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 18,
        flexWrap: "wrap",
        justifyContent: "flex-start",
        minWidth: 0,
      }}
    >
      {leftContent}
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "center",
        minWidth: 0,
      }}
    >
      {centerContent}
    </div>

    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 16,
        flexWrap: "wrap",
        minWidth: 0,
      }}
    >
      {rightContent}
    </div>
  </div>
);

const MetricInline = ({ label, value, valueColor = T.text }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 6,
    }}
  >
    <span
      style={{
        fontSize: "0.72rem",
        color: T.verySoft,
        lineHeight: 1,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "0.82rem",
        color: valueColor,
        lineHeight: 1,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  </div>
);

const ViewSwitch = ({ viewMode, setViewMode }) => {
  const options = ["Vue séquence", "Vue critique", "Vue interfaces"];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flexWrap: "nowrap",
      }}
    >
      {options.map((option) => {
        const active = option === viewMode;

        return (
          <button
            key={option}
            type="button"
            onClick={() => setViewMode(option)}
            style={{
              height: 28,
              padding: "0 10px",
              borderRadius: 999,
              border: `1px solid ${active ? "rgba(14,63,86,0.18)" : T.border}`,
              background: active ? "rgba(14,63,86,0.05)" : "#ffffff",
              color: active ? T.corelia : T.soft,
              fontSize: "0.71rem",
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

const GraphFilterSwitch = ({ graphFilter, setGraphFilter }) => {
  const options = ["Tous", "Flux", "Interfaces", "Critiques"];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        flexWrap: "nowrap",
      }}
    >
      {options.map((option) => {
        const active = option === graphFilter;

        return (
          <button
            key={option}
            type="button"
            onClick={() => setGraphFilter(option)}
            style={{
              height: 28,
              padding: "0 10px",
              borderRadius: 999,
              border: `1px solid ${active ? "rgba(14,63,86,0.18)" : T.border}`,
              background: active ? "rgba(14,63,86,0.05)" : "#ffffff",
              color: active ? T.corelia : T.soft,
              fontSize: "0.71rem",
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
};

const LegendDot = ({ color, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div
      style={{
        width: 10,
        height: 10,
        borderRadius: 999,
        background: color,
      }}
    />
    <span style={{ fontSize: "0.72rem", color: T.verySoft }}>{label}</span>
  </div>
);

const LinkLegendItem = ({ color, dashed = false, thick = false, label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
    <div
      style={{
        width: 18,
        height: 0,
        borderTop: `${thick ? 3 : 2}px ${dashed ? "dashed" : "solid"} ${color}`,
      }}
    />
    <span style={{ fontSize: "0.72rem", color: T.verySoft }}>{label}</span>
  </div>
);

const StageColumn = ({ title, packages, activeId, onSelect, viewMode }) => (
  <div
    style={{
      minWidth: 305,
      width: 305,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusXl,
      background: "linear-gradient(180deg, rgba(252,253,255,1) 0%, rgba(248,250,252,1) 100%)",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      flexShrink: 0,
    }}
  >
    <div
      style={{
        padding: "12px 12px 10px 12px",
        borderBottom: `1px solid ${T.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <div
        style={{
          fontSize: "0.70rem",
          color: T.verySoft,
          textTransform: "uppercase",
          letterSpacing: "0.03em",
          lineHeight: 1,
        }}
      >
        Séquence
      </div>

      <div
        style={{
          fontSize: "0.82rem",
          color: T.text,
          lineHeight: 1.2,
          fontWeight: 500,
        }}
      >
        {title}
      </div>

      <div
        style={{
          fontSize: "0.68rem",
          color: T.soft,
          lineHeight: 1.25,
        }}
      >
        {packages.length} package{packages.length > 1 ? "s" : ""}
      </div>
    </div>

    <div
      style={{
        padding: 10,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minHeight: 300,
      }}
    >
      {packages.map((pkg) => (
        <PackageSequenceCard
          key={pkg.id}
          pkg={pkg}
          isActive={pkg.id === activeId}
          onSelect={onSelect}
          viewMode={viewMode}
        />
      ))}
    </div>
  </div>
);

const PackageSequenceCard = ({ pkg, isActive, onSelect, viewMode }) => {
  const flowTone = getFlowTone(pkg);

  return (
    <div
      onClick={() => onSelect(pkg)}
      style={{
        border: `1px solid ${isActive ? "rgba(14,63,86,0.18)" : T.border}`,
        borderRadius: T.radiusLg,
        background: "#ffffff",
        boxShadow: isActive ? T.shadowSm : T.shadowXs,
        padding: "10px 10px 9px 10px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        cursor: "pointer",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
            minWidth: 0,
          }}
        >
          <div
            style={{
              fontSize: "0.76rem",
              color: T.text,
              lineHeight: 1.22,
              fontWeight: 500,
            }}
          >
            {pkg.name}
          </div>

          <div
            style={{
              fontSize: "0.66rem",
              color: T.soft,
              lineHeight: 1.2,
            }}
          >
            {pkg.type} · {pkg.logic}
          </div>
        </div>

        <div
          style={{
            flexShrink: 0,
            fontSize: "0.65rem",
            color: getStatusColor(pkg.status),
            lineHeight: 1,
          }}
        >
          {pkg.status}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        <span
          style={{
            fontSize: "0.64rem",
            color: getDisciplineColor(pkg.discipline),
            lineHeight: 1,
          }}
        >
          {pkg.discipline}
        </span>

        <span
          style={{
            fontSize: "0.64rem",
            color: T.verySoft,
            lineHeight: 1,
          }}
        >
          {pkg.zone}
        </span>

        <span
          style={{
            fontSize: "0.64rem",
            color: getCriticalityColor(pkg.criticality),
            lineHeight: 1,
          }}
        >
          {pkg.criticality}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          paddingTop: 1,
        }}
      >
        <MiniMetric label="Amont" value={pkg.dependencyIds.length} />
        <MiniMetric label="Aval" value={pkg.downstreamIds.length} />
        <MiniMetric label="Éléments" value={pkg.itemsCount} />
      </div>

      <div
        style={{
          fontSize: "0.67rem",
          lineHeight: 1.25,
          color:
            viewMode === "Vue critique"
              ? getCriticalityColor(pkg.criticality)
              : viewMode === "Vue interfaces"
              ? T.caneva
              : T.corelia,
        }}
      >
        {viewMode === "Vue critique"
          ? `Criticité ${pkg.criticality}`
          : viewMode === "Vue interfaces"
          ? `${pkg.dependencyIds.length + pkg.downstreamIds.length} interfaces de séquence`
          : `${flowTone} · ${pkg.executionMode}`}
      </div>
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 5,
    }}
  >
    <span
      style={{
        fontSize: "0.64rem",
        color: T.verySoft,
        lineHeight: 1,
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "0.70rem",
        color: T.text,
        lineHeight: 1,
        fontWeight: 600,
      }}
    >
      {value}
    </span>
  </div>
);

const SequencingGraph = ({
  packages,
  activeId,
  onSelect,
  graphFilter,
}) => {
  const colGap = 170;
  const rowGap = 90;
  const startX = 90;
  const startY = 70;
  const nodeR = 18;

  const packageMap = useMemo(
    () => Object.fromEntries(packages.map((pkg) => [pkg.id, pkg])),
    [packages]
  );

  const nodes = useMemo(
    () =>
      packages.map((pkg) => ({
        ...pkg,
        x: startX + pkg.graph.col * colGap,
        y: startY + pkg.graph.row * rowGap,
      })),
    [packages]
  );

  const links = useMemo(() => {
    const baseLinks = [];

    packages.forEach((targetPkg) => {
      (targetPkg.dependencyIds || []).forEach((sourceId) => {
        const sourcePkg = packageMap[sourceId];
        if (!sourcePkg) return;

        baseLinks.push({
          id: `${sourceId}-${targetPkg.id}`,
          sourceId,
          targetId: targetPkg.id,
          type: getLinkType(sourcePkg, targetPkg),
        });
      });
    });

    if (graphFilter === "Flux") {
      return baseLinks.filter((l) => l.type === "flow");
    }
    if (graphFilter === "Interfaces") {
      return baseLinks.filter((l) => l.type === "interface");
    }
    if (graphFilter === "Critiques") {
      return baseLinks.filter((l) => l.type === "critical");
    }
    return baseLinks;
  }, [packages, packageMap, graphFilter]);

  const nodeById = useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n])),
    [nodes]
  );

  const svgWidth = 760;
  const svgHeight = 360;

  const getLinkStroke = (type) => {
    if (type === "critical") return T.caneva;
    if (type === "interface") return T.orangeText;
    return T.corelia;
  };

  const getLinkDash = (type) => {
    if (type === "interface") return "6 4";
    return "0";
  };

  const getLinkWidth = (type) => {
    if (type === "critical") return 3;
    return 2;
  };

  return (
    <div
      style={{
        width: "100%",
        // border: `1px solid ${T.border}`,
        borderRadius: T.radiusXl,
        // background: "linear-gradient(180deg, rgba(252,253,255,1) 0%, rgba(248,250,252,1) 100%)",
        padding: 12,
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          paddingBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: "0.70rem",
            color: T.verySoft,
            textTransform: "uppercase",
            letterSpacing: "0.03em",
            lineHeight: 1,
          }}
        >
          Sequencing graph
        </div>

        <div
          style={{
            fontSize: "0.80rem",
            color: T.text,
            lineHeight: 1.2,
            fontWeight: 500,
          }}
        >
          Graphe synthétique des packages
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          style={{
            display: "block",
            minWidth: svgWidth,
          }}
        >
          {/* repères de colonnes */}
          {stageOrder.map((stage, index) => {
            const x = startX + index * colGap;

            return (
              <g key={stage}>
                <text
                  x={x}
                  y={24}
                  textAnchor="middle"
                  fontSize="11"
                  fill={T.verySoft}
                  style={{ fontFamily: theme?.fonts?.body || "inherit" }}
                >
                  {stage}
                </text>
              </g>
            );
          })}

          {/* liens */}
          {links.map((link) => {
            const source = nodeById[link.sourceId];
            const target = nodeById[link.targetId];
            if (!source || !target) return null;

            return (
              <line
                key={link.id}
                x1={source.x + nodeR}
                y1={source.y}
                x2={target.x - nodeR}
                y2={target.y}
                stroke={getLinkStroke(link.type)}
                strokeWidth={getLinkWidth(link.type)}
                strokeDasharray={getLinkDash(link.type)}
                strokeLinecap="round"
              />
            );
          })}

          {/* nœuds */}
          {nodes.map((node) => {
            const active = node.id === activeId;

            return (
              <g
                key={node.id}
                onClick={() => onSelect(node)}
                style={{ cursor: "pointer" }}
              >
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={active ? 20 : 18}
                  fill="#ffffff"
                  stroke={active ? T.corelia : getCriticalityColor(node.criticality)}
                  strokeWidth={active ? 2.5 : 2}
                />
                <text
                  x={node.x}
                  y={node.y + 4}
                  textAnchor="middle"
                  fontSize="13"
                  fontWeight="600"
                  fill={T.text}
                  style={{ fontFamily: theme?.fonts?.body || "inherit" }}
                >
                  {node.code}
                </text>
                <text
                  x={node.x}
                  y={node.y + 32}
                  textAnchor="middle"
                  fontSize="11"
                  fill={T.soft}
                  style={{ fontFamily: theme?.fonts?.body || "inherit" }}
                >
                  {node.code} · {node.stage}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
};

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const ActivitySequencingBoard = ({ onSelectPackage }) => {
  const [viewMode, setViewMode] = useState("Vue séquence");
  const [graphFilter, setGraphFilter] = useState("Tous");
  const [packages] = useState(stablePackages);
  const [activeId, setActiveId] = useState(stablePackages[0]?.id || null);

  const stageGroups = useMemo(() => {
    return stageOrder.map((stage) => ({
      stage,
      packages: packages.filter((pkg) => pkg.stage === stage),
    }));
  }, [packages]);

  const activePackage = useMemo(() => {
    return packages.find((pkg) => pkg.id === activeId) || packages[0] || null;
  }, [packages, activeId]);

  const criticalCount = useMemo(
    () => packages.filter((pkg) => pkg.criticality === "Critique").length,
    [packages]
  );

  const startCount = useMemo(
    () => packages.filter((pkg) => !pkg.dependencyIds?.length).length,
    [packages]
  );

  const graphLinkCount = useMemo(() => {
    let count = 0;
    packages.forEach((pkg) => {
      count += (pkg.dependencyIds || []).length;
    });
    return count;
  }, [packages]);

  const handleSelect = (pkg) => {
    setActiveId(pkg.id);
    if (onSelectPackage) onSelectPackage(pkg);
  };

  useEffect(() => {
    if (activePackage && onSelectPackage) {
      onSelectPackage(activePackage);
    }
  }, [activePackage, onSelectPackage]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <HeaderBlock
        packageCount={packages.length}
        criticalCount={criticalCount}
        startCount={startCount}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      <div
        style={{
          width: "100%",
          display: "flex",
          gap: 14,
          overflowX: "auto",
          paddingBottom: 18,
          alignItems: "stretch",
        }}
      >
        {stageGroups.map(({ stage, packages: stagePackages }) => (
          <StageColumn
            key={stage}
            title={stage}
            packages={stagePackages}
            activeId={activeId}
            onSelect={handleSelect}
            viewMode={viewMode}
          />
        ))}
      </div>

      <GraphToolbar
        nodeCount={packages.length}
        linkCount={graphLinkCount}
        graphFilter={graphFilter}
        setGraphFilter={setGraphFilter}
      />

      <SequencingGraph
        packages={packages}
        activeId={activeId}
        onSelect={handleSelect}
        graphFilter={graphFilter}
      />
    </div>
  );
};

export default ActivitySequencingBoard;